import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { Product } from "@/types";
import { getPriceRange } from "@/types";
import { useCostMarkups, getCostMarkupsForCategories } from "@/contexts/CostMarkupContext";
import { useHomepageProducts } from "@/contexts/HomepageProductsContext";

function normalizeCategories(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((v) => typeof v === "string" && v.length > 0);
  if (raw && typeof raw === "object") {
    return Object.values(raw as Record<string, unknown>).filter(
      (v): v is string => typeof v === "string" && v.length > 0
    );
  }
  return [];
}

export default function FeaturedProducts() {
  const costMarkupMap = useCostMarkups();
  const { featured: rawProducts } = useHomepageProducts();

  // Embaralha a ordem dos produtos a cada renderização/visita para rotacionar o destaque
  const products = useMemo(() => {
    if (rawProducts.length <= 1) return rawProducts;
    const list = [...rawProducts];
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const itemI = list[i];
      const itemJ = list[j];
      if (itemI && itemJ) {
        list[i] = itemJ;
        list[j] = itemI;
      }
    }
    return list;
  }, [rawProducts]);

  if (products.length === 0) return null;

  const formatPrice = (v: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

  const renderCard = (product: Product, idx: number, key: string) => {
    const custom = product.customizationCost;
    const defaultCustomizationCost =
      custom?.silk_screen ??
      custom?.uv_print ??
      custom?.laser ??
      0;

    const priceRange = getPriceRange(
      product.variants[0]?.price ?? 0,
      defaultCustomizationCost,
      product.pricingTiers,
      getCostMarkupsForCategories(costMarkupMap, normalizeCategories(product.categories))
    );

    return (
      <Link
        key={key}
        to={`/produto/${product.id}`}
        className="group relative block w-44 shrink-0 overflow-hidden rounded-xl border border-border bg-bg transition-all duration-normal hover:border-border-hover hover:shadow-card-hover sm:w-52"
      >
        {idx === 0 && (
          <span className="absolute left-2 top-2 z-10 flex items-center gap-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-md">
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61z" />
            </svg>
            Top
          </span>
        )}

        <div className="aspect-square overflow-hidden rounded-t-xl bg-surface p-3 sm:p-4">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="h-full w-full object-contain transition-transform duration-normal group-hover:scale-105"
            loading="lazy"
          />
        </div>

        <div className="p-2.5 sm:p-3">
          <h3 className="text-xs font-semibold text-text-primary line-clamp-2 sm:text-sm">
            {product.name}
          </h3>

          {priceRange ? (
            <div className="mt-2 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 px-2.5 py-1.5">
              <p className="text-[9px] font-medium uppercase tracking-wide text-emerald-600">A partir de</p>
              <p className="text-sm font-extrabold leading-tight text-emerald-700 sm:text-base">
                {formatPrice(priceRange.min)}
              </p>
            </div>
          ) : (
            <p className="mt-2 text-xs text-text-muted">Consulte</p>
          )}

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const msg = encodeURIComponent(
                `Olá! Tenho interesse no produto "${product.name}" e gostaria de solicitar uma cotação.`
              );
              window.open(`https://wa.me/?text=${msg}`, "_blank");
            }}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#25D366] px-2.5 py-1.5 text-[10px] font-bold text-white shadow-sm transition-all hover:bg-[#1da851] hover:shadow-md active:scale-95 sm:text-xs"
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Pedir Cotação
          </button>
        </div>
      </Link>
    );
  };

  return (
    <section className="mb-8 sm:mb-12">
      <div className="mb-4 flex items-center justify-between px-4 sm:mb-6 sm:px-0">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-red-500 text-white">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 23c-3.6 0-8-2.4-8-7.7C4 10 12 1 12 1s8 9 8 14.3c0 5.3-4.4 7.7-8 7.7zm0-18.5C9.6 7.8 6 13.1 6 15.3 6 19.3 9 21 12 21s6-1.7 6-5.7c0-2.2-3.6-7.5-6-10.8z" />
              <path d="M12 21c-1.7 0-3.5-.8-3.5-3.2 0-2.2 2.5-5.5 3.5-7 1 1.5 3.5 4.8 3.5 7 0 2.4-1.8 3.2-3.5 3.2z" />
            </svg>
          </span>
          <h2 className="text-lg font-bold text-text-primary sm:text-xl">
            Mais Vendidos
          </h2>
        </div>

        {/* Setas de navegação */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("featured-scroll-container");
              if (el) el.scrollBy({ left: -240, behavior: "smooth" });
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-surface text-text-secondary transition-colors hover:border-accent hover:text-accent active:scale-95"
            title="Anterior"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("featured-scroll-container");
              if (el) el.scrollBy({ left: 240, behavior: "smooth" });
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-surface text-text-secondary transition-colors hover:border-accent hover:text-accent active:scale-95"
            title="Próximo"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      <div
        id="featured-scroll-container"
        className="no-scrollbar flex cursor-grab gap-3 overflow-x-auto active:cursor-grabbing scroll-smooth px-4 sm:px-0"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {products.map((product, idx) => (
          <div key={product.id} className="shrink-0">
            {renderCard(product, idx, product.id)}
          </div>
        ))}
      </div>
    </section>
  );
}
